'use client';

import * as React from 'react';
import { Loader2 } from 'lucide-react';
import { PhoneInput } from './PhoneInput';
import { SmsOtpInput } from './SmsOtpInput';
import { cn } from '@/lib/utils';
export interface SmsLoginFormProps {
  /** Destination after successful verify. Defaults to `/`. */
  callbackURL?: string;
  /** Optional external error sink (in addition to the inline alert). */
  onError?: (message: string) => void;
  className?: string;
}
type Step = 'phone' | 'otp';
const RESEND_SECONDS = 60;
const PHONE_LEN = 11;
const OTP_LEN = 4;

/**
 * Format an 11-digit normalized phone (`79XXXXXXXXX`) to display form
 * `+7 (925) 123-45-67`. For shorter input returns the partial display.
 */
function displayPhone(phone: string): string {
  const digits = (phone || '').replace(/\D/g, '');
  if (!digits) return '';
  // `normalizePhone` always yields `7` + 10 digits; do the same visual formatting here.
  const rest = digits.startsWith('7') ? digits.slice(1) : digits;
  const parts = ['+7'];
  if (rest.length > 0) parts.push(`(${rest.slice(0, 3)})`);
  if (rest.length > 3) parts[parts.length - 1] += ''; // noop, keep clarity
  if (rest.length >= 3) {
    const mid = rest.slice(3, 6);
    const tail = rest.slice(6);
    let s = parts.join(' ');
    if (mid) s += ` ${mid}`;
    if (tail.length > 0) s += `-${tail.slice(0, 2)}`;
    if (tail.length > 2) s += `-${tail.slice(2, 4)}`;
    return s;
  }
  return parts.join(' ');
}
const SEND_OTP_ERROR_MAP: Record<number, string> = {
  400: 'Введите корректный номер',
  404: 'SMS-вход временно отключён',
  429: 'Подождите минуту перед следующей отправкой',
  503: 'SMS-сервис временно недоступен, попробуйте позже'
};
const VERIFY_ERROR_MAP: Record<number, string> = {
  400: 'Некорректные данные',
  401: 'Неверный код',
  410: 'Код истёк, запросите новый',
  429: 'Слишком много попыток. Попробуйте позже'
};
const SEND_FALLBACK = 'Не удалось отправить код. Попробуйте позже';
const VERIFY_FALLBACK = 'Не удалось проверить код. Попробуйте позже';
interface SendOtpResult {
  ok: boolean;
  errorMessage?: string;
}

/**
 * SMS login form — two-step flow (phone → OTP). Uses `/api/auth/sms/send-otp`
 * and `/api/auth/sms/verify`. After successful verify does a hard
 * `window.location.href` so Better Auth Set-Cookie gets picked up on reload.
 */
export function SmsLoginForm({
  callbackURL,
  onError,
  className
}: SmsLoginFormProps) {
  const [step, setStep] = React.useState<Step>('phone');
  const [phone, setPhone] = React.useState('');
  const [code, setCode] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [verifying, setVerifying] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [resendSeconds, setResendSeconds] = React.useState(0);
  const otpRef = React.useRef<HTMLInputElement>(null);
  const cancelledRef = React.useRef(false);

  // Cancel any in-flight state updates on unmount.
  React.useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  // Autofocus OTP input when entering step 2.
  React.useEffect(() => {
    if (step === 'otp') {
      // rAF so the element is mounted.
      const id = requestAnimationFrame(() => {
        otpRef.current?.focus();
      });
      return () => cancelAnimationFrame(id);
    }
  }, [step]);

  // Resend countdown.
  React.useEffect(() => {
    if (step !== 'otp' || resendSeconds <= 0) return;
    const id_0 = setInterval(() => {
      setResendSeconds(s => s > 0 ? s - 1 : 0);
    }, 1000);
    return () => clearInterval(id_0);
  }, [step, resendSeconds]);
  const reportError = React.useCallback((msg: string) => {
    setError(msg);
    onError?.(msg);
  }, [onError]);
  const sendOtp = React.useCallback(async (targetPhone: string): Promise<SendOtpResult> => {
    let response: Response;
    try {
      response = await fetch('/api/auth/sms/send-otp', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          phone: `+${targetPhone}`
        })
      });
    } catch {
      return {
        ok: false,
        errorMessage: SEND_FALLBACK
      };
    }
    if (response.ok) return {
      ok: true
    };
    let serverMessage: string | undefined;
    try {
      const data = (await response.json()) as {
        error?: string;
      };
      if (data && typeof data.error === 'string') serverMessage = data.error;
    } catch {
      /* ignore — use fallback from map */
    }
    const mapped = SEND_OTP_ERROR_MAP[response.status];
    return {
      ok: false,
      errorMessage: serverMessage || mapped || SEND_FALLBACK
    };
  }, []);
  const handlePhoneSubmit = React.useCallback(async () => {
    if (phone.length !== PHONE_LEN || sending) return;
    setSending(true);
    setError(null);
    const result = await sendOtp(phone);
    if (cancelledRef.current) return;
    setSending(false);
    if (result.ok) {
      setStep('otp');
      setResendSeconds(RESEND_SECONDS);
      setCode('');
      setError(null);
    } else if (result.errorMessage) {
      reportError(result.errorMessage);
    }
  }, [phone, sending, sendOtp, reportError]);
  const handleResend = React.useCallback(async () => {
    if (resendSeconds > 0 || sending) return;
    setSending(true);
    setError(null);
    const result_0 = await sendOtp(phone);
    if (cancelledRef.current) return;
    setSending(false);
    if (result_0.ok) {
      setResendSeconds(RESEND_SECONDS);
      setCode('');
    } else if (result_0.errorMessage) {
      reportError(result_0.errorMessage);
    }
  }, [phone, resendSeconds, sending, sendOtp, reportError]);
  const handleChangePhone = React.useCallback(() => {
    setStep('phone');
    setCode('');
    setError(null);
    setResendSeconds(0);
  }, []);
  const verifyOtp = React.useCallback(async (submittedCode: string) => {
    if (verifying) return;
    setVerifying(true);
    setError(null);
    let response_0: Response;
    try {
      response_0 = await fetch('/api/auth/sms/verify', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          phone: `+${phone}`,
          code: submittedCode
        })
      });
    } catch {
      if (!cancelledRef.current) {
        setVerifying(false);
        reportError(VERIFY_FALLBACK);
      }
      return;
    }
    if (response_0.ok) {
      // Hard reload so Better Auth Set-Cookie takes effect. Same pattern as TelegramLoginButton.
      window.location.href = callbackURL || '/';
      return;
    }
    let serverMessage_0: string | undefined;
    try {
      const data_0 = (await response_0.json()) as {
        error?: string;
      };
      if (data_0 && typeof data_0.error === 'string') serverMessage_0 = data_0.error;
    } catch {
      /* ignore */
    }
    if (cancelledRef.current) return;
    setVerifying(false);
    const mapped_0 = VERIFY_ERROR_MAP[response_0.status];
    const msg_0 = mapped_0 || serverMessage_0 || VERIFY_FALLBACK;
    reportError(msg_0);

    // Reset input on verify-specific errors so user can retype.
    if (response_0.status === 401 || response_0.status === 410) {
      setCode('');
      // Return focus to OTP input on wrong/expired code.
      requestAnimationFrame(() => otpRef.current?.focus());
    }
  }, [callbackURL, phone, verifying, reportError]);
  const handleOtpComplete = React.useCallback((submittedCode_0: string) => {
    void verifyOtp(submittedCode_0);
  }, [verifyOtp]);
  const phoneValid = phone.length === PHONE_LEN;
  const submitDisabled = !phoneValid || sending;
  return <div className={cn('flex flex-col gap-3', className)}>
      {step === 'phone' ? <form onSubmit={e => {
      e.preventDefault();
      void handlePhoneSubmit();
    }} className="flex flex-col gap-3" noValidate>
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="text-foreground/80">Номер телефона</span>
            <PhoneInput value={phone} onChange={setPhone} disabled={sending} aria-invalid={Boolean(error)} autoFocus />
          </label>

          {error && <p role="alert" aria-live="polite" className="text-sm text-destructive">
              {error}
            </p>}

          <button type="submit" disabled={submitDisabled} className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer">
            {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <span aria-hidden>📞</span>}
            По СМС коду
          </button>
        </form> : <div className="flex flex-col gap-3">
          <p className="text-sm text-muted-foreground text-center">
            Код отправлен на {displayPhone(phone)}
          </p>

          <SmsOtpInput ref={otpRef} value={code} onChange={setCode} onComplete={handleOtpComplete} disabled={verifying} aria-invalid={Boolean(error)} />

          {error && <p role="alert" aria-live="polite" className="text-sm text-destructive text-center">
              {error}
            </p>}

          {verifying && <p className="text-xs text-muted-foreground text-center animate-pulse">
              Проверяем код…
            </p>}

          <div className="flex flex-col items-center gap-2 text-sm">
            <button type="button" onClick={() => void handleResend()} disabled={resendSeconds > 0 || sending} className="text-foreground/70 hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer">
              {resendSeconds > 0 ? `Отправить ещё раз через ${resendSeconds}с` : 'Отправить ещё раз'}
            </button>
            <button type="button" onClick={handleChangePhone} className="text-muted-foreground hover:text-foreground underline-offset-4 hover:underline cursor-pointer">
              Ввести другой номер
            </button>
          </div>
        </div>}
    </div>;
}

// Re-export formatter for tests / external callers.
export { displayPhone as formatPhoneForSmsLabel };

// Exported only for tests that want to assert fallback copy.
export const __testConstants = {
  SEND_OTP_ERROR_MAP,
  VERIFY_ERROR_MAP,
  RESEND_SECONDS,
  PHONE_LEN,
  OTP_LEN
};